import { ContactInput, type ResolvedQuestion } from '@aera/schema';
import { useTranslation } from 'react-i18next';

/**
 * The API's own rule (ContactInput.email, Zod's email check), so the browser flags exactly what the
 * server would reject: a looser check lets an address through that then fails the whole contact save.
 */
export const isEmailValid = (email: string) => ContactInput.shape.email.safeParse(email).success;

export const isContactEmpty = (c: ContactInput | null) =>
  !c || !['name', 'company', 'phoneOrWechat', 'email', 'bestTime'].some((k) => (c[k as keyof ContactInput] ?? '').trim());

/**
 * I4: the one form. Stored encrypted on the session and never sent to the model. Phone or email, not both,
 * and nothing is required: leaving it empty shows the session code to quote instead.
 */
export function ContactQuestion({
  rq,
  value,
  onChange,
  sessionCode,
}: {
  rq: ResolvedQuestion;
  value: ContactInput | null;
  onChange: (next: ContactInput, opts?: { debounce?: boolean }) => void;
  sessionCode: string;
}) {
  const { t } = useTranslation();
  const v = value ?? {};
  const set = (key: keyof ContactInput, text: string) => onChange({ ...v, [key]: text }, { debounce: true });
  const emailBad = contactEmailInvalid(v);

  const field = (key: keyof ContactInput, label: string, opts: { type?: string; placeholder?: string; autoComplete?: string; inputMode?: 'tel' | 'email' | 'text' } = {}) => (
    <div>
      <label htmlFor={`contact-${key}`} className="mb-1.5 block text-[14px] text-ink-2">
        {label}
      </label>
      <input
        id={`contact-${key}`}
        type={opts.type ?? 'text'}
        inputMode={opts.inputMode}
        autoComplete={opts.autoComplete}
        maxLength={key === 'company' ? 120 : key === 'email' ? 200 : 60}
        value={v[key] ?? ''}
        placeholder={opts.placeholder}
        aria-invalid={key === 'email' && emailBad ? true : undefined}
        aria-describedby={key === 'email' && emailBad ? 'contact-email-error' : undefined}
        onChange={(e) => set(key, e.target.value)}
        className="w-full rounded-2xl border border-hair bg-white px-4 py-3 text-[16px] outline-none transition-colors placeholder:text-ink-3 focus:border-indigo aria-[invalid=true]:border-red-400"
      />
    </div>
  );

  return (
    <div className="space-y-4">
      {field('name', t('question.contact.name'), { placeholder: t('question.contact.namePlaceholder'), autoComplete: 'name' })}
      {field('company', t('question.contact.company'), { autoComplete: 'organization' })}
      {field('phoneOrWechat', t('question.contact.phoneOrWechat'), { inputMode: 'tel', autoComplete: 'tel' })}
      {field('email', t('question.contact.email'), { type: 'email', inputMode: 'email', autoComplete: 'email' })}
      {emailBad && (
        <p id="contact-email-error" className="-mt-2 text-[13px] text-red-700">
          {t('question.contact.emailInvalid')}
        </p>
      )}
      {field('bestTime', t('question.contact.bestTime'), { placeholder: t('question.contact.bestTimePlaceholder') })}
      {rq.hint && <p className="text-[13px] text-ink-2">{rq.hint}</p>}
      {isContactEmpty(value) && (
        <p className="rounded-2xl bg-surface px-4 py-3 text-[14px] text-ink-2">
          {t('question.contact.sessionCode')} <span className="lp-mono font-semibold text-black">{sessionCode}</span>
        </p>
      )}
    </div>
  );
}

export const contactEmailInvalid = (c: ContactInput | null) => !!c?.email && !isEmailValid(c.email);
